import { useState, useEffect } from 'react';
import { Alert, AlertTitle, Grid, Paper, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { selectCustomer, updateCustomer } from '@ameroservices-platform/loppe-boothownerui/app/main/apps/globalStore';
import firebaseService from '@ameroservices-platform/shared/services/firebase';
import { useForm } from 'react-hook-form';
import { GetRuleByName } from '@ameroservices-platform/shared/utility/formValidation/HookFormRules';
import TextFieldForm from '@ameroservices-platform/shared/ui-components/fields/TextFieldForm';
import { showMessage } from '@ameroservices-platform/loppe-boothownerui/app/store/fuse/messageSlice';
import Typography from '@mui/material/Typography';
import { LoadingButton } from '@mui/lab';
import ConfirmationDialog from '@ameroservices-platform/loppe-boothownerui/app/ui-components/ConfirmationDialog';
import { EmailAuthProvider } from 'firebase/auth';

function Profile() {
	const selectedCustomer = useSelector(selectCustomer);
	const dispatch = useDispatch();
	const [changePasswordDialog, setChangePasswordDialog] = useState(false);
	const [changePasswordError, setChangePasswordError] = useState(null);
	const [oldPassword, setOldPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmNewPassword, setConfirmNewPassword] = useState('');

	const { handleSubmit, reset, formState, control } = useForm({
		mode: 'onChange',
		defaultValues: {
			firstname: selectedCustomer?.contactInfo?.billing?.firstname || '',
			lastname: selectedCustomer?.contactInfo?.billing?.lastname || '',
			email: selectedCustomer?.contactInfo?.billing?.email || '',
			phone: selectedCustomer?.contactInfo?.billing?.phone || '',
			address: selectedCustomer?.contactInfo?.billing?.address || '',
			postalCode: selectedCustomer?.contactInfo?.billing?.postalCode || '',
			city: selectedCustomer?.contactInfo?.billing?.city || '',
			accNumber: selectedCustomer?.contactInfo?.billing?.accNumber || '',
			regNumber: selectedCustomer?.contactInfo?.billing?.regNumber || ''
		}
	});
	const { isValid, isSubmitting } = formState;

	useEffect(() => {
		if (selectedCustomer) {
			reset({
				firstname: selectedCustomer?.contactInfo?.billing?.firstname || '',
				lastname: selectedCustomer?.contactInfo?.billing?.lastname || '',
				email: selectedCustomer?.contactInfo?.billing?.email || '',
				phone: selectedCustomer?.contactInfo?.billing?.phone || '',
				address: selectedCustomer?.contactInfo?.billing?.address || '',
				postalCode: selectedCustomer?.contactInfo?.billing?.postalCode || '',
				city: selectedCustomer?.contactInfo?.billing?.city || '',
				accNumber: selectedCustomer?.contactInfo?.billing?.accNumber || '',
				regNumber: selectedCustomer?.contactInfo?.billing?.regNumber || ''
			});
		}
	}, [selectedCustomer, reset]);

	function handleChangePasswordClick() {
		setChangePasswordDialog(true);
	}

	async function handleChangePassword(result) {
		setChangePasswordError(null);
		if (result) {
			if (newPassword !== confirmNewPassword) {
				setChangePasswordError('NOT_SAME_PASSWORD');
				return;
			}
			if (newPassword === oldPassword) {
				setChangePasswordError('SAME_PASSWORD');
				return;
			}
			try {
				await firebaseService.auth.currentUser.reauthenticateWithCredential(
					EmailAuthProvider.credential(selectedCustomer.contactInfo.billing.email, oldPassword)
				);
			} catch (e) {
				setChangePasswordError('WRONG_PASSWORD');
				return;
			}
			try {
				await firebaseService.auth.currentUser.updatePassword(newPassword);
			} catch (e) {
				console.error(e);
				setChangePasswordError('UNKNOWN');
				return;
			}
			dispatch(
				showMessage({
					message: 'Din adgangskode er blevet opdateret',
					variant: 'success'
				})
			);
		}
		setOldPassword('');
		setNewPassword('');
		setConfirmNewPassword('');
		setChangePasswordDialog(false);
	}

	async function onSubmit(data) {
		try {
			const res = await updateCustomer({
				contactInfo: data
			});
			if (res.data.error === false) {
				dispatch(
					showMessage({
						message: 'Din profil er blevet opdateret',
						variant: 'success'
					})
				);
			} else {
				console.error(res.data);
				dispatch(
					showMessage({
						message: 'Der opstod en fejl under opdatering af din profil',
						variant: 'error'
					})
				);
			}
		} catch (e) {
			console.error(e);
			dispatch(
				showMessage({
					message: 'Der opstod en fejl under opdatering af din profil',
					variant: 'error'
				})
			);
		}
	}

	return (
		<div className="px-10 md:mx-auto w-full md:w-lg">
			<Paper className="mt-48 p-10 sm:p-20 w-full">
				<form onSubmit={handleSubmit(onSubmit)}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Typography variant={'h6'}>Min profil</Typography>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextFieldForm
								label={'Fornavn'}
								name={'firstname'}
								control={control}
								validations={GetRuleByName('firstname')}
								size={'small'}
								required
								disabled={isSubmitting}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextFieldForm
								label={'Efternavn'}
								name={'lastname'}
								control={control}
								validations={GetRuleByName('lastname')}
								size={'small'}
								required
								disabled={isSubmitting}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextFieldForm
								label={'Email'}
								name={'email'}
								control={control}
								validations={GetRuleByName('email')}
								size={'small'}
								disabled
								required
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextFieldForm
								label={'Telefonnummer'}
								name={'phone'}
								control={control}
								validations={GetRuleByName('phone')}
								size={'small'}
								required
								disabled={isSubmitting}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextFieldForm
								label={'Adresse'}
								name={'address'}
								control={control}
								validations={GetRuleByName('address')}
								size={'small'}
								required
								disabled={isSubmitting}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextFieldForm
								label={'Postnummer'}
								name={'postalCode'}
								control={control}
								validations={GetRuleByName('postalCode')}
								size={'small'}
								required
								disabled={isSubmitting}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextFieldForm
								label={'By'}
								name={'city'}
								control={control}
								validations={GetRuleByName('city')}
								size={'small'}
								required
								disabled={isSubmitting}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextFieldForm
								label={'Registreringsnummer'}
								name={'regNumber'}
								control={control}
								validations={GetRuleByName('regi')}
								size={'small'}
								required
								disabled={isSubmitting}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextFieldForm
								label={'Kontonummer'}
								name={'accNumber'}
								control={control}
								validations={GetRuleByName('accNumber')}
								size={'small'}
								required
								disabled={isSubmitting}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<div className={'flex sm:justify-start'}>
								<LoadingButton
									className={'rounded-md w-full sm:w-auto'}
									variant={'contained'}
									onClick={handleChangePasswordClick}
								>
									Ændre adgangskode
								</LoadingButton>
							</div>
						</Grid>
						<Grid item xs={12} sm={6}>
							<div className={'flex sm:justify-end'}>
								<LoadingButton
									type={'submit'}
									className={'rounded-md w-full sm:w-auto'}
									variant={'contained'}
									disabled={!isValid}
									loading={isSubmitting}
								>
									Gem ændringer
								</LoadingButton>
							</div>
						</Grid>
					</Grid>
				</form>
			</Paper>
			<ConfirmationDialog
				open={changePasswordDialog}
				onClose={handleChangePassword}
				okButton="Ændre adgangskode"
				cancelButton="Annuller"
				maxWidth={'sm'}
				fullWidth
				simple
				title={'Ændre adgangskode'}
				disableButtons={!oldPassword || !newPassword || !confirmNewPassword}
				content={
					<Grid container spacing={2}>
						{changePasswordError && (
							<Grid item xs={12}>
								<Alert severity={'error'} className={'mb-10'}>
									<AlertTitle>Fejl</AlertTitle>
									{changePasswordError === 'NOT_SAME_PASSWORD' && (
										<span>Adgangskoderne er ikke ens</span>
									)}
									{changePasswordError === 'SAME_PASSWORD' && (
										<span>Den nye adgangskode må ikke være den samme som den gamle</span>
									)}
									{changePasswordError === 'WRONG_PASSWORD' && (
										<span>Den gamle adgangskode er forkert</span>
									)}
									{changePasswordError === 'UNKNOWN' && (
										<span>Der opstod en ukendt fejl, prøv igen senere</span>
									)}
								</Alert>
							</Grid>
						)}
						<Grid item xs={12} sm={6}>
							<TextField
								label={'Ny adgangskode'}
								type={'password'}
								size={'small'}
								required
								className={'w-full'}
								onChange={e => setNewPassword(e.target.value)}
								value={newPassword}
								error={newPassword && newPassword.length <= 5}
								helperText={
									newPassword && newPassword.length <= 5 ? 'Adgangskode skal være mindst 6 tegn' : ''
								}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label={'Bekræft adgangskode'}
								type={'password'}
								size={'small'}
								required
								className={'w-full'}
								onChange={e => setConfirmNewPassword(e.target.value)}
								value={confirmNewPassword}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label={'Nuværende adgangskode'}
								type={'password'}
								size={'small'}
								required
								className={'w-full'}
								onChange={e => setOldPassword(e.target.value)}
								value={oldPassword}
							/>
						</Grid>
					</Grid>
				}
			/>
		</div>
	);
}

export default Profile;
